import type { ReactElement } from "react";
import { useCallback, useDeferredValue, useMemo, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Combobox, DropdownButton, Tooltip } from "@similarweb/ui-components";

import { abbrNumberFilter } from "app/filters/ngFilters";
import { StyledBaseDropdownButtonWrap } from "app/pages/sales-intelligence/common-components/dropdown/ControlledMultiDropdown/styles";

import type { IDynamicListOption } from "../../types/webhook";
import { StyledWebhookCellWrapper } from "../styles";

import ListDropdownEmptyState from "./ListDropdownEmptyState";

interface IListDropdownCellProps {
    value: string;
    index: number;
    options: readonly IDynamicListOption[];
    onChange: (index: number, field: "queryId", value: string) => void;
}

const MAX_RESULTS_THRESHOLD = 100_000;

const ListDropdownCell = ({ value, index, options, onChange }: IListDropdownCellProps): ReactElement => {
    const { translate } = useTranslate();
    const formatCount = useMemo(() => abbrNumberFilter(), []);
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const deferredSearchTerm = useDeferredValue(searchTerm);

    const optionsMap = useMemo(() => new Map(options.map((opt) => [opt.id, opt])), [options]);

    const filteredOptions = useMemo(() => {
        const term = deferredSearchTerm.toLowerCase();
        if (!term) {
            return options;
        }

        return options.filter((opt) => {
            if (opt.name.toLowerCase().includes(term)) {
                return true;
            }
            return opt.ownerUserName?.toLowerCase().includes(term) ?? false;
        });
    }, [options, deferredSearchTerm]);

    const buttonText = useMemo(() => {
        if (!value) {
            return translate("si.management.saved-search-webhooks.list-placeholder");
        }
        return optionsMap.get(value)?.name ?? value;
    }, [value, optionsMap, translate]);

    const handleSelect = useCallback(
        (opt: IDynamicListOption) => {
            if (opt.totalCount > MAX_RESULTS_THRESHOLD || opt.id === value) {
                return;
            }
            onChange(index, "queryId", opt.id);
            setIsOpen(false);
            setSearchTerm("");
        },
        [value, index, onChange],
    );

    const handleOpenChange = useCallback((open: boolean) => {
        setIsOpen(open);
        if (!open) {
            setSearchTerm("");
        }
    }, []);

    return (
        <StyledWebhookCellWrapper>
            <Combobox
                isOpen={isOpen}
                onOpenChange={handleOpenChange}
                data-automation="saved-search-webhooks-list-dropdown"
            >
                <Combobox.Trigger>
                    <StyledBaseDropdownButtonWrap buttonTextColor={colorsPalettes.carbon[400]}>
                        <DropdownButton>{buttonText}</DropdownButton>
                    </StyledBaseDropdownButtonWrap>
                </Combobox.Trigger>

                <Combobox.SearchInput
                    value={searchTerm}
                    onChange={setSearchTerm}
                    placeholder={translate("si.management.saved-search-webhooks.list-search-placeholder")}
                />

                <Combobox.Content emptyState={<ListDropdownEmptyState />}>
                    {filteredOptions.map((opt) => {
                        const count = opt.totalCount;
                        const isDisabled = count > MAX_RESULTS_THRESHOLD;
                        const formattedCount = formatCount(count);
                        const label = opt.ownerUserName
                            ? `${opt.name} · ${opt.ownerUserName} · ${formattedCount}`
                            : `${opt.name} · ${formattedCount}`;

                        const optionContent = <Combobox.OptionLabel>{label}</Combobox.OptionLabel>;

                        return (
                            <Combobox.Option
                                key={opt.id}
                                id={opt.id}
                                textValue={opt.name}
                                isDisabled={isDisabled}
                                isSelected={opt.id === value}
                                onClick={() => handleSelect(opt)}
                            >
                                {isDisabled ? (
                                    <Tooltip
                                        content={translate("si.management.saved-search-webhooks.list-exceeds-tooltip")}
                                        placement="top"
                                    >
                                        <div>{optionContent}</div>
                                    </Tooltip>
                                ) : (
                                    optionContent
                                )}
                            </Combobox.Option>
                        );
                    })}
                </Combobox.Content>
            </Combobox>
        </StyledWebhookCellWrapper>
    );
};

export default ListDropdownCell;
